import { cx } from 'class-variance-authority';
import { useState } from 'react';

import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import Motion from '@/app/components/Motion';
import MotionSafeVideo from '@/app/components/MotionSafeVideo';

import type { PopHighlightedFeaturesSectionProps } from './PopHighlightedFeaturesSection';
import { getSectionMediaMotionProps } from './utils/getSectionMediaMotionProps';

export interface PopHighlightedFeaturesSectionMediaProps
  extends Pick<
    PopHighlightedFeaturesSectionProps,
    'activeIndex' | 'id' | 'image' | 'index' | 'video'
  > {
  readonly isInView: boolean;
  readonly isSticky: boolean;
}

const PopHighlightedFeaturesSectionMedia = ({
  activeIndex,
  id,
  image,
  index,
  isInView,
  isSticky,
  video,
}: PopHighlightedFeaturesSectionMediaProps) => {
  const isVisible =
    (!isSticky && isInView) || (isSticky && activeIndex >= index);
  const motionProps = getSectionMediaMotionProps({ isVisible });
  const [isOutOfView, setIsOutOfView] = useState<boolean>(false);

  // Reset isOutOfView state on animation start so that we always see media transitioning
  const handleAnimationStart = () => {
    setIsOutOfView(false);
  };

  // Set isOutOfView state based on isVisible to prevent any of the hidden media from being shown
  const handleAnimationComplete = () => {
    setIsOutOfView(!isVisible);
  };

  return (
    <div
      className="relative h-[70vh] max-h-[470px] overflow-hidden rounded-lg md:aspect-3/2 md:h-auto md:max-h-[70vh] md:rounded-2xl lg:ml-auto lg:aspect-4/3"
      data-cy="pop-highlighted-features-media"
    >
      <Motion
        className={cx(
          'size-full motion-safe:[--highlights-section-media-y-from:0] lg:motion-safe:[--highlights-section-media-y-from:100%]',
          {
            invisible: isOutOfView,
          },
        )}
        onAnimationComplete={handleAnimationComplete}
        onAnimationStart={handleAnimationStart}
        {...motionProps}
      >
        {image && (
          <ArtDirectionImage
            className="size-full object-cover object-center"
            fill
            sizes="(max-width: 1023px) 95vw, (max-width: 1279px) 49vw, (max-width: 1567px) 62vw, 982px"
            {...image}
          />
        )}
        {video && (
          <MotionSafeVideo
            image={{
              ...video.image,
              className: 'size-full object-cover object-center',
              fill: true,
              sizes:
                '(max-width: 1023px) 95vw, (max-width: 1279px) 49vw, (max-width: 1567px) 62vw, 982px',
            }}
            video={{
              ...video.video,
              autoPlay: true,
              className: 'size-full object-cover object-center',
              controls: false,
              location: `pop-highlighted-features-section-${id}`,
              loop: true,
              muted: true,
              playsInline: true,
              type: 'video/mp4',
            }}
          />
        )}
      </Motion>
    </div>
  );
};

export default PopHighlightedFeaturesSectionMedia;
